// What shows while the app goes through Keycloak, and when sign-in fails. Signing in normally takes a moment;
// if it is still going after SLOW_AFTER_MS, the page says so and offers Try again rather than waiting forever.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SignInScreen.module.css';

export const SLOW_AFTER_MS = 8000;

type Props = { onRetry: () => void } & ({ error?: undefined } | { error: string });

export function SignInScreen({ onRetry, error }: Props) {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (error !== undefined) return;
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [error]);

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <p className={styles.logo} aria-hidden="true">
          Xpedition<span className={styles.logoAccent}>2</span>
        </p>

        {error === undefined ? (
          <div className={styles.status} role="status">
            <span className={styles.spinner} aria-hidden="true" />
            <p className={styles.message}>{t('auth.signingIn')}</p>
            {slow && (
              <>
                <p className={styles.hint}>{t('auth.slow')}</p>
                <button type="button" className={styles.button} onClick={onRetry}>
                  {t('auth.retry')}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className={styles.card} role="alert">
            <h1 className={styles.title}>{t('auth.errorTitle')}</h1>
            <p className={styles.reason}>{t('auth.error', { message: error })}</p>
            <button type="button" className={styles.button} onClick={onRetry}>
              {t('auth.retry')}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
